import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/Dialog';
import type { Signal } from '@momentum/types';
import { SignalApplicabilityScope } from '@momentum/types';
import { useDeactivateSignal } from '@/shared/hooks/useSignals';

interface DeactivateConfirmDialogProps {
  signal: Signal | null;
  onClose: () => void;
  currentUserId: string;
}

export const DeactivateConfirmDialog = ({
  signal,
  onClose,
  currentUserId,
}: DeactivateConfirmDialogProps) => {
  const { t } = useTranslation('knowledge');
  const deactivateMutation = useDeactivateSignal();

  const open = !!signal;

  const appliedCount = useMemo(() => {
    if (!signal) return 0;

    return (
      signal.installed?.users?.filter(
        (installedUser) => installedUser.appliedBy?.toString() === currentUserId
      ).length ?? 0
    );
  }, [signal, currentUserId]);

  const scopeLabel = useMemo(() => {
    if (!signal) return '';

    const operation = signal.applyOperations?.find(
      (op) => op.managerId === currentUserId
    );
    return operation?.scope === SignalApplicabilityScope.FULL_HIERARCHY
      ? t('appliedToFullHierarchy')
      : t('appliedToDirectReports');
  }, [signal, currentUserId, t]);

  const handleConfirm = async () => {
    if (!signal) return;

    try {
      await deactivateMutation.mutateAsync(signal._id);
      onClose();
    } catch {
      toast.error(t('deactivateSignalError'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('deactivateDialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('deactivateDialogAppliedText')(scopeLabel, appliedCount)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={deactivateMutation.isPending}
          >
            {t('deactivateDialogCancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending
              ? t('deactivateDialogDeactivating')
              : t('deactivateDialogConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
